/** Downscale an image to a max-edge of `maxEdge` pixels and return it as a
 *  base64-encoded JPEG suitable for posting to an inference-platform
 *  endpoint. `maxEdge` is required and belongs to the task being called —
 *  see DETECTOR_MAX_EDGE_PX in tasks/detect-openings.js for why the Step-1
 *  detector picks the value it does. Resizing client-side also saves upload
 *  bytes on the way to the backend.
 *
 *  Accepts a File/Blob, an HTMLImageElement, or a data: URL string.
 *  Returns just the base64 payload (no `data:` prefix), matching what
 *  detectOpenings() expects for its imageB64 arg.
 */
export async function downscaleToB64(source, maxEdge, jpegQuality = 0.92) {
  const img = await loadImage(source);
  const ratio = Math.min(
    maxEdge / img.naturalWidth,
    maxEdge / img.naturalHeight,
    1,
  );
  const w = Math.round(img.naturalWidth * ratio);
  const h = Math.round(img.naturalHeight * ratio);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);
  const dataUrl = canvas.toDataURL("image/jpeg", jpegQuality);
  return { base64: dataUrl.split(",")[1] };
}

async function loadImage(source) {
  if (typeof HTMLImageElement !== "undefined" && source instanceof HTMLImageElement) {
    if (source.complete && source.naturalWidth > 0) return source;
    await new Promise((res, rej) => {
      source.onload = res;
      source.onerror = rej;
    });
    return source;
  }
  const url =
    typeof source === "string"
      ? source
      : URL.createObjectURL(source);
  try {
    const img = new Image();
    await new Promise((res, rej) => {
      img.onload = res;
      img.onerror = rej;
      img.src = url;
    });
    return img;
  } finally {
    if (typeof source !== "string") URL.revokeObjectURL(url);
  }
}
